import type React from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";

interface StreamData {
  id?: number;
  title: string;
  start: string;
  end: string;
  date: string;
  venueName?: string;
  addressOne: string;
  addressTwo: string;
  suburb: string;
  city: string;
  [key: string]: any;
}

interface StreamsSectionProps {
  streams: StreamData[];
  currentTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onViewStream: () => void;
  onActionClick: (actionType: string) => void;
  disableActions: boolean;
  bookingService?: string;
  onVenueHover?: () => void;
  onVenueLeave?: () => void;
  onVenueClick?: () => void;
}

const StreamsSection: React.FC<StreamsSectionProps> = ({
  streams,
  currentTab,
  onTabChange,
  onViewStream,
  onActionClick,
  disableActions,
  bookingService,
  onVenueHover,
  onVenueLeave,
  onVenueClick,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper sx={{ p: 2, boxShadow: 0, height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {streams.length > 0 ? (
          <Tabs
            value={currentTab}
            onChange={onTabChange}
            TabIndicatorProps={{
              style: {
                backgroundColor: colors.primary[400],
                height: 2,
              },
            }}
            sx={{
              ".MuiTab-root": {
                color: colors.primary[300],
              },
              ".Mui-selected": {
                color: colors.primary[400],
              },
            }}
          >
            {streams.map((_, idx) => (
              <Tab key={idx} label={`Stream ${idx + 1}`} />
            ))}
          </Tabs>
        ) : (
          <Typography variant="subtitle1" color="textSecondary">
            No streams available for this booking.
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={onViewStream}
          disabled={!streams[currentTab]?.id}
        >
          View stream
        </Button>
      </Box>

      {/* Stream Details */}
      <Box>
        {streams.length > 0 ? (
          streams.map((stream, idx) =>
            currentTab === idx ? (
              <Grid container spacing={1} mt={1} key={idx}>
                {Object.entries(stream).map(([label, value], i) => (
                  <Grid size={{ xs: 3 }} key={i}>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.primary[300] }}
                    >
                      {label.charAt(0).toUpperCase() + label.slice(1)}
                    </Typography>

                    {/* Hover only on venueName */}
                    {label === "venueName" ? (
                      <Typography
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        onMouseEnter={onVenueHover}
                        onMouseLeave={onVenueLeave}
                        onClick={onVenueClick}
                      >
                        {String(value)}
                      </Typography>
                    ) : (
                      <Typography>{String(value)}</Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            ) : null
          )
        ) : (
          <Box mt={2}>
            <Typography color="textSecondary">
              This booking does not have any associated streams.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box mt={2} display="flex" gap={2} pt={2}>
        <Button
          variant="contained"
          onClick={() => onActionClick("Callsheet")}
          disabled={disableActions}
        >
          Callsheet
        </Button>

        {bookingService === "Conference" && (
          <Button
            variant="contained"
            onClick={() => onActionClick("Stream Page")}
            disabled={disableActions}
          >
            Stream Page
          </Button>
        )}

        {bookingService === "Church" && (
          <Button
            variant="contained"
            onClick={() => onActionClick("Church Page")}
            disabled={disableActions}
          >
            Stream Page
          </Button>
        )}

        {bookingService !== "Conference" && bookingService !== "Church" && (
          <Button
            variant="contained"
            onClick={() => onActionClick("Memorial Page")}
            disabled={disableActions}
          >
            Memorial Page
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default StreamsSection;
